import { AppTopBarActions } from "~/components/appTopBarActionSlot";
import { SidebarInset } from "../ui/sidebar";
import { useProjectTaskChrome } from "../tasks/useProjectTaskChrome";
import { CmmdProjectActiveTab } from "./CmmdProjectHomePanels";
import type { CmmdProjectHomeViewProps } from "./CmmdProjectHomeTypes";
import { CmmdProjectTabs } from "./CmmdProjectTabs";
import { ProjectLeadBadge } from "./ProjectLeadBadge";
import { projectDisplayName } from "./projectDisplayName";
import { ProjectLeadSheet } from "./ProjectLeadSheet";
import { renderProjectTabTrailing } from "./projectTabTrailing";
import { resolveCmmdProjectId } from "./cmmdProjectId";

export type { CmmdProjectHomeViewProps } from "./CmmdProjectHomeTypes";

export function CmmdProjectHomeView(props: CmmdProjectHomeViewProps) {
  const projectId = resolveCmmdProjectId(props.home.project?.id);
  const taskChrome = useProjectTaskChrome({
    projectId,
    routeViewState: props.taskViewRoute,
    onViewStateChange: props.onTaskViewRouteChange,
    enabled: projectId != null,
  });

  return (
    <SidebarInset className="h-full min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden bg-background">
        {/* The badge sits in the global bar beside the breadcrumb that already
            names this project, rather than in a header row of its own. It was
            that row's only occupant, so the row went with it. */}
        <AppTopBarActions>
          <ProjectLeadBadge
            status={props.projectLeadStatus}
            onOpenProjectLead={() => props.onTaskSelectionChange({ kind: "projectLead" })}
          />
        </AppTopBarActions>
        <ProjectLeadSheet
          projectId={props.home.project ? String(props.home.project.id) : ""}
          projectTitle={projectDisplayName(props.home.project) ?? "Project"}
          open={props.taskSelection.kind === "projectLead" && props.home.project !== null}
          onClose={() => props.onTaskSelectionChange({ kind: "none" })}
          onPlanWork={() => {
            props.onTaskSelectionChange({ kind: "none" });
            props.onTabChange("tasks");
            taskChrome.setComposerOpen(true);
          }}
        />
        <CmmdProjectTabs
          activeTab={props.activeTab}
          onTabChange={props.onTabChange}
          trailing={renderProjectTabTrailing({
            activeTab: props.activeTab,
            taskChrome: projectId != null ? taskChrome : null,
            tasks: props.taskBoard.tasks,
            onRefreshAutomations: props.onRefreshAutomations,
            ...(projectId ? { projectId } : {}),
            onWorkflowSaved: props.taskBoard.reload,
            workflowAvailable: props.taskBoard.workflow != null,
            taskMutationsDisabled: props.taskBoard.workflowMutationBlocked,
          })}
        />
        <CmmdProjectContent {...props} taskChrome={taskChrome} />
      </div>
    </SidebarInset>
  );
}

function CmmdProjectContent(
  props: CmmdProjectHomeViewProps & {
    readonly taskChrome: ReturnType<typeof useProjectTaskChrome>;
  },
) {
  if (props.home.loading) {
    return (
      <div className="flex min-h-[200px] flex-1 items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading project…</p>
      </div>
    );
  }
  if (props.home.error) {
    return (
      <div className="flex min-h-[200px] flex-1 flex-col items-center justify-center gap-2 p-6">
        <p className="text-sm text-destructive">{props.home.error}</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">
        <CmmdProjectActiveTab {...props} taskChrome={props.taskChrome} />
      </div>
    </div>
  );
}
